import { useEffect, useRef, useState } from 'react';
import { generateQuestions } from 'services/campaign/api';
import { formatGeneratedQuestions } from 'pages/CampaignCreation/utils/formatGeneratedQuestions';
import { ToastType, useToast } from 'services/toast';
import { v4 as uuidv4 } from 'uuid';
import { useCampaignCreationStore } from 'modules/campaign-creation/stores/useCampaignCreationStore';
import {
  CampaignStatus,
  CampaignType,
  JobDescriptionParsed
} from 'generated-graphql/types';
import { QuestionWithId } from 'pages/CampaignCreation/types';
import {
  useCreateCampaignMutation,
  useStructureJobDescriptionParsedLazyQuery
} from 'generated-graphql/hooks/apollo-hooks';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { useUser } from 'core/stores/hooks/useUser';
import { mapQuestionsPayloadV3 } from 'modules/campaign-creation/utils/mapQuestionsPayloadV3';
import { AnalysingLoadingGif } from 'common/animations/AnalysingLoadingGif';
import { Stack, Typography } from '@mui/material';
import { Check } from '@mui/icons-material';
import { LoadingGif } from 'common/animations/LoadingGif';
import { Paths } from 'services/navigation/paths';
import { useNavigate } from 'react-router-dom';

export const CampaignCreationGenerateJobDescriptionAndQuestions: React.FC =
  () => {
    const navigate = useNavigate();

    const [loadingItems, setLoadingItems] = useState<string[]>([
      'Analysing reference material'
    ]);
    const organization = useOrganization();
    const user = useUser();
    const [createCampaign] = useCreateCampaignMutation();
    const [structureJobDescription] =
      useStructureJobDescriptionParsedLazyQuery();
    const campaignId = useCampaignCreationStore((store) => store.campaignId);
    const state = useCampaignCreationStore((store) => store.state);
    const onAIGenerateJobDescription = useCampaignCreationStore(
      (store) => store.onAIGenerateJobDescription
    );
    const onAIGenerateQuestions = useCampaignCreationStore(
      (store) => store.onAIGenerateQuestions
    );
    const onAIGenerateJobDescriptionAndQuestionsFinished =
      useCampaignCreationStore(
        (store) => store.onAIGenerateJobDescriptionAndQuestionsFinished
      );

    const { showToast } = useToast();

    const getGenerationText = (): string => {
      if (state?.campaignType === CampaignType.EngagementOutreach) {
        return state?.engagementDescriptionState?.outreachDescription ?? '';
      }

      const jobDescriptionText =
        state?.noATSJobDescriptionState?.jobDescriptionText ??
        state?.ATSJobDescriptionState?.selectedJob?.jobDescriptionText ??
        '';

      return jobDescriptionText;
    };

    const handleGenerateJobDescription = async (
      jobDescriptionText: string
    ): Promise<JobDescriptionParsed | null> => {
      if (
        state?.campaignType === CampaignType.EngagementOutreach ||
        state?.jobDescriptionParsedState?.jobDescriptionParsed
      ) {
        return state?.jobDescriptionParsedState?.jobDescriptionParsed || null;
      }

      const { data } = await structureJobDescription({
        variables: { content: jobDescriptionText }
      });

      const result = data?.structureJobDescriptionParsed?.jobDescription;

      if (result) {
        const jobDescriptionParsed: JobDescriptionParsed = {
          jobTitle: result.jobTitle || '',
          employerName: result.employerName || '',
          location: result.location || '',
          summaryOfRole: result.summaryOfRole || '',
          contractType: result.contractType || ''
        };
        onAIGenerateJobDescription({ jobDescriptionParsed });
        return jobDescriptionParsed;
      }

      return null;
    };

    const handleGenerateQuestions = async (
      jobDescriptionText: string
    ): Promise<QuestionWithId[]> => {
      if (state?.questions) {
        return state?.questions;
      }

      const result = await generateQuestions(jobDescriptionText);
      const formattedQuestions = formatGeneratedQuestions(result);
      const questionsWithId = formattedQuestions.map((q) => ({
        ...q,
        id: uuidv4()
      }));
      onAIGenerateQuestions({
        questions: questionsWithId
      });
      return questionsWithId;
    };

    const startedRef = useRef(false);

    useEffect(() => {
      if (startedRef.current) {
        return;
      }
      startedRef.current = true;

      const handleGenerateJobDescriptionAndQuestions = async (
        jobDescriptionText: string
      ) => {
        try {
          const jobDescriptionParsed = await handleGenerateJobDescription(
            jobDescriptionText
          );
          const questions = await handleGenerateQuestions(jobDescriptionText);
          if (campaignId) {
            onAIGenerateJobDescriptionAndQuestionsFinished({
              campaignId
            });
            return;
          }
          const result = await createCampaign({
            variables: {
              input: {
                // CORE
                status: CampaignStatus.Draft,
                userId: user.id,
                organizationId: organization.id,
                // CHOOSE_TYPE STEP
                type: state?.campaignType,
                // ENGAGEMENT_DESCRIPTION STEP
                outreachDescription:
                  state?.engagementDescriptionState?.outreachDescription ||
                  null,
                title:
                  state?.engagementDescriptionState?.title ||
                  jobDescriptionParsed?.jobTitle ||
                  undefined,
                // JOB_DESCRIPTION STEP
                jobDescriptionText,
                // NO_ATS_JOB_DESCRIPTION STEP
                externalId:
                  state?.noATSJobDescriptionState?.externalId || undefined,
                // ATS_JOB_DESCRIPTION STEP
                atsJobId:
                  state?.ATSJobDescriptionState?.selectedJob?.atsJobId || null,
                atsJobPostingId:
                  state?.ATSJobDescriptionState?.selectedJob?.atsJobPostingId ||
                  null,
                // EDIT_JOB_DESCRIPTION STEP
                jobDescriptionParsed: jobDescriptionParsed,
                // QUESTIONS_AND_DOCUMENTS STEP
                questions: mapQuestionsPayloadV3(questions),
                // ATS_IMPORT STEP
                atsListId: state?.ATSImportState?.atsListId || null
              }
            }
          });
          if (!result.data?.createCampaign?.id) {
            throw new Error('Could not create campaign');
          }
          // NOTE: Instead we redirect to campaign edit then the store will pick everything up from there
          navigate(
            `${Paths.CAMPAIGN_CREATE_DRAFT}/${result.data.createCampaign.id}`,
            {
              replace: true
            }
          );
        } catch (e) {
          showToast({
            message: 'Could not create draft campaign',
            type: ToastType.Error
          });
        }
      };
      const text = getGenerationText();
      handleGenerateJobDescriptionAndQuestions(text);
    }, []);

    useEffect(() => {
      const interval = setInterval(() => {
        setLoadingItems((prev) => {
          const newItems = [...prev];
          if (newItems.length === 1) {
            newItems.push('Creating discussion guide');
            return newItems;
          }
          if (newItems.length === 2) {
            newItems.push('Generating questions');
            return newItems;
          }
          if (newItems.length === 3) {
            newItems.push('Saving draft campaign');
          }
          clearInterval(interval);
          return newItems;
        });
      }, 3000);
      return () => clearInterval(interval);
    }, []);

    return (
      <Stack gap="20px" width="600px" sx={{ margin: '100px auto' }}>
        {loadingItems.map((item, index) => (
          <Stack key={item} gap="20px" direction="row" alignItems="center">
            {loadingItems.length - 1 === index && index !== 3 && (
              <AnalysingLoadingGif />
            )}
            {loadingItems.length - 1 === index && index === 3 && <LoadingGif />}
            {loadingItems.length - 1 !== index && (
              <Check
                sx={{
                  color: 'successV2.main'
                }}
                style={{
                  width: '60px',
                  height: '60px'
                }}
              />
            )}
            <Typography variant="heading">{item}</Typography>
          </Stack>
        ))}
      </Stack>
    );
  };
